import { useState } from 'react';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import {
  Box,
  Button,
  Card,
  Typography,
  CircularProgress,
  CardContent,
  Chip,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Grid from '@mui/material/Grid';
import {
  Receipt,
  CheckCircle,
  Error,
  HourglassEmpty,
  Download,
  Business,
  Assessment,
  Cancel,
} from '@mui/icons-material';
import { useSuperadminTransactionReport } from '../../hooks/useSuperAdmin';
import { colors } from '../../theme/theme';
import { DateFilter, DateFilterValue } from '../../components/common';

export const TransactionsReportPage = () => {
  const [dateFilter, setDateFilter] = useState<DateFilterValue>('all');
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();

  const { report, isLoading, error } = useSuperadminTransactionReport({
    date_filter: dateFilter,
    start_date: startDate,
    end_date: endDate,
  });

  const handleDateFilterChange = (filter: DateFilterValue, start?: string, end?: string) => {
    setDateFilter(filter);
    setStartDate(start);
    setEndDate(end);
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <Alert severity="error">Failed to load transaction report</Alert>
      </AdminLayout>
    );
  }

  const countyColumns: GridColDef[] = [
    {
      field: 'county_name',
      headerName: 'County',
      flex: 1,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Business sx={{ fontSize: 20, color: colors.primary.main }} />
          <Typography fontWeight={500}>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'total',
      headerName: 'Total',
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: 'rgba(102, 126, 234, 0.1)',
            color: colors.primary.main,
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      field: 'successful',
      headerName: 'Successful',
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: 'rgba(76, 175, 80, 0.1)',
            color: colors.success.main,
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      field: 'failed',
      headerName: 'Failed',
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: 'rgba(244, 67, 54, 0.1)',
            color: colors.error.main,
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      field: 'pending',
      headerName: 'Pending',
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: 'rgba(255, 152, 0, 0.1)',
            color: colors.warning.main,
            fontWeight: 600,
          }}
        />
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={600}>
            System Transaction Reports
          </Typography>
          <Box display="flex" gap={2}>
            <DateFilter
              value={dateFilter}
              onChange={handleDateFilterChange}
              startDate={startDate}
              endDate={endDate}
            />
            <Button
              variant="contained"
              startIcon={<Download />}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #65408b 100%)',
                },
              }}
            >
              Export Report
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3} mb={3}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Total Transactions
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="white" mt={1}>
                      {report?.totalTransactions || 0}
                    </Typography>
                  </Box>
                  <Assessment sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Successful
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="white" mt={1}>
                      {report?.successfulTransactions || 0}
                    </Typography>
                  </Box>
                  <CheckCircle sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #f44336 0%, #c62828 100%)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Failed
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="white" mt={1}>
                      {report?.failedTransactions || 0}
                    </Typography>
                  </Box>
                  <Cancel sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Pending
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="white" mt={1}>
                      {report?.pendingTransactions || 0}
                    </Typography>
                  </Box>
                  <HourglassEmpty sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Transactions by County
            </Typography>
            {report?.transactionsByCounty && report.transactionsByCounty.length > 0 ? (
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGrid
                  rows={report.transactionsByCounty}
                  columns={countyColumns}
                  getRowId={(row) => row.county_id}
                  pageSizeOptions={[5, 10, 25]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                  }}
                  disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-row:hover': {
                      bgcolor: 'rgba(102, 126, 234, 0.04)',
                    },
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No transaction data available for the selected period
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
};
